import { useMemo, type ReactNode } from 'react';
import {
  EntityDetailProvider,
  EntityPeekHost,
  type EntityDetailLinkProps,
} from '@locus-taxy/entity-detail';
import { relatedGridSlot } from '@locus-taxy/entity-detail/grid-adapter';
import {
  compactRelatedDetailsRenderer,
  detailFormRenderer,
} from '@locus-taxy/entity-detail/related-details';
import {
  DetailFormRendererProvider,
  RelatedDetailsRendererProvider,
} from '@locus-taxy/encore-ui-toolkit/core';
import { DashboardLink } from './DashboardLink';
import { PeekCustomSectionSlot } from '../customApps/PeekCustomSection';
import { useCurrentUsecase } from '../hooks/useCurrentUsecase';
import { useClientId } from '../hooks/useClientId';
import { getApiBaseUrl, GOOGLE_MAPS_API_KEY } from '../config';

/**
 * `<DashboardLink>` adapted to the framework's minimal `LinkComponent`
 * contract. The framework only needs `to` + `children`; the link accepts both
 * (its `to` is string-compatible), so this is a thin pass-through — and going
 * through `<DashboardLink>` rather than a bare `<Link>` means every link the
 * framework renders inside a detail route inherits the current query
 * (DP-3188 AC8).
 */
function RouterLink({
  to,
  children,
  ...rest
}: Readonly<EntityDetailLinkProps>) {
  return (
    <DashboardLink to={to} {...rest}>
      {children}
    </DashboardLink>
  );
}

/**
 * Mounts {@link EntityDetailProvider} with the dashboard's app-level config —
 * API base URL, active client, and react-router's `Link`. As of
 * `@locus-taxy/entity-detail` 1.1.0 the framework consumes the shared
 * encore-ui-network singleton (its `AuthProvider` sets the `On-Behalf-Of`
 * header) and the host's **ambient** react-query cache, matching encore-grid —
 * so no `onBehalfOf` / `queryClient` are threaded. Must still render inside
 * `NetworkProvider` (which owns the `QueryClientProvider` + `AuthProvider`).
 * Precedent: the `GridEntityPickerProvider` mount in `main.tsx`.
 */
export function EntityDetailFrameworkProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const clientId = useClientId();
  /*
   * Usecase (DP-3570) from the URL, so every metadata read the framework makes internally — the
   * tab strip, section dispatch, the details app decision — resolves the usecase's view-config
   * document. Without it a usecase details page renders the entity's DEFAULT layout and masks the
   * usecase's own detailsView (PRD AC-9). Derived here rather than threaded per call site because
   * those reads live inside the framework, out of the consumer's reach.
   */
  const usecase = useCurrentUsecase();

  const apiBaseUrl = useMemo(
    () => (clientId ? getApiBaseUrl(clientId) : ''),
    [clientId]
  );

  return (
    <EntityDetailProvider
      apiBaseUrl={apiBaseUrl}
      clientId={clientId}
      usecase={usecase}
      LinkComponent={RouterLink}
      googleApiKey={GOOGLE_MAPS_API_KEY}
    >
      {/*
       * One app-wide peek host. The panel used to be mounted only by
       * `EntityDetailView`, so it existed on detail routes alone: an entity
       * GRID that wanted the same quick-view had to mount its own host and
       * hand-write cell renderers (which is what hub-ops did). With the host
       * here, anything below it — every grid with `peek`, every `entityRef`
       * link — opens the same panel, and the peeked record's related grids
       * render through the same adapter the detail page uses.
       */}
      {/*
       * And the map popover's `relatedDetails` sections. encore-grid renders
       * that popover but must not import entity-detail (it would close a
       * dependency cycle), so the resolver is registered here and the grid
       * asks the toolkit for it. Unregistered ⇒ those sections render empty,
       * exactly as before.
       */}
      <RelatedDetailsRendererProvider render={compactRelatedDetailsRenderer}>
        {/* Same cycle break for the grid's object-array dialog; unregistered ⇒ JSON. */}
        <DetailFormRendererProvider render={detailFormRenderer}>
          {/*
           * `custom` sections too, not just the grid: a peeked record draws the
           * same `detailsView.tabs[]` the full page draws, so a `custom` section
           * needs the dashboard's MFE renderer — scoped to the PEEKED record, not
           * to whatever page is underneath. Without it `SectionDispatcher` fails
           * that kind closed and the tab renders a placeholder. `relatedDetails`
           * needs nothing here: the host already defaults that slot to the
           * framework's core renderer, same as the full page.
           */}
          <EntityPeekHost
            relatedGridSlot={relatedGridSlot}
            renderCustomSection={PeekCustomSectionSlot}
          >
            {children}
          </EntityPeekHost>
        </DetailFormRendererProvider>
      </RelatedDetailsRendererProvider>
    </EntityDetailProvider>
  );
}

export default EntityDetailFrameworkProvider;
